import BaseModel3d from './BaseModel3d';
import { pixelRatio3d } from '@/editor/math/const';
import { globalStores } from '@/editor/stores';
import { disposeAllChildren, disposeAllChildrenThenRemoveFromParent, mirrorObject } from '../utils';
import { IUpdateModelParams } from '@/editor/data/model/model';
import { BoxGeometry, Group, Mesh, MeshBasicMaterial } from 'three';

const BOUNDING_BOX_OFFSET = 0.01;

class Model3d extends BaseModel3d {
  protected isFlipped: boolean = false;

  protected intersectModel: Group = new Group();

  constructor(id: string) {
    super({ id });
  }

  private updateBrandgoodInfo(brandgoodId: string) {
    const brandgoodInfo = globalStores.modelStore.getModel(brandgoodId);
    if (!brandgoodInfo) return;

    this._modelUrl = brandgoodInfo.model3d;
    this._originBoundingBox = [
      brandgoodInfo.properties.width,
      brandgoodInfo.properties.thickness,
      brandgoodInfo.properties.height
    ];
    this._originRotationZ = brandgoodInfo.properties.rotationZ || 0;
  }

  update(params: Omit<IUpdateModelParams, 'model2d' | 'model3d'>) {
    if (!this.canUpdate) return;

    const { modelId: brandgoodId } = params;

    this.clearModel();

    this.updateBrandgoodInfo(brandgoodId);
    // 添加包围盒
    this.createBoundingBox();
    this.updatePlaceholder();

    this.updateWithoutDestroy(params);
  }

  updateWithoutDestroy(
    params: Pick<IUpdateModelParams, 'center' | 'width' | 'thickness' | 'height' | 'rotation' | 'offGround' | 'flip'>
  ) {
    if (!this.canUpdate) return;
    const { center, width, thickness, height, rotation, offGround, flip } = params;

    // 位置x,y调整
    this.model.position.set(center.x / pixelRatio3d, center.y / pixelRatio3d, (offGround + height / 2) / pixelRatio3d);
    this.intersectModel.position.set(
      center.x / pixelRatio3d,
      center.y / pixelRatio3d,
      (offGround + height / 2) / pixelRatio3d
    );

    // 缩放
    this.model.scale.set(
      width / this._originBoundingBox[0],
      thickness / this._originBoundingBox[1],
      height / this._originBoundingBox[2]
    );
    this.intersectModel.scale.set(
      width / this._originBoundingBox[0],
      thickness / this._originBoundingBox[1],
      height / this._originBoundingBox[2]
    );

    // 旋转
    this.model.rotation.z = rotation;
    this.intersectModel.rotation.z = rotation;

    if (!this.isLoaded) {
      void this.loadModel().then((attached) => {
        if (flip && !this.isFlipped && attached) {
          this.isFlipped = true;
          mirrorObject(this.model);
        }
      });
    } else {
      if (flip && !this.isFlipped) {
        this.isFlipped = true;
        mirrorObject(this.model);
      }
    }
  }

  override setVisible(visible: boolean) {
    this.model.visible = visible;
  }

  private createBoundingBox() {
    const width = this._originBoundingBox[0];
    const thickness = this._originBoundingBox[1];
    const height = this._originBoundingBox[2];

    const box = new Mesh(
      new BoxGeometry(
        width / pixelRatio3d + BOUNDING_BOX_OFFSET,
        thickness / pixelRatio3d + BOUNDING_BOX_OFFSET,
        height / pixelRatio3d + BOUNDING_BOX_OFFSET
      ),
      new MeshBasicMaterial({ opacity: 0, transparent: true, depthWrite: false })
    );
    this.intersectModel.add(box);
  }

  override clearModel(): void {
    super.clearModel();
    disposeAllChildren(this.intersectModel);
    this.isFlipped = false;
  }

  override dispose() {
    disposeAllChildrenThenRemoveFromParent(this.intersectModel);
    super.dispose();
  }
}

export default Model3d;
